<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-vue-next';

interface Deck {
  id: string;
  name: string;
  isDefault: boolean;
  curriculumId: string | null;
  archivedAt: string | null;
  cardCount: number;
  dueCount: number;
}

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const decks = ref<Deck[]>([]);
const loading = ref(false);
const error = ref('');
const nameInput = ref('');
const creating = ref(false);

// Default first, then curriculum-owned decks, then plain user decks — each group
// alphabetical. (Curriculum decks are the tutor's; plain decks are the user's own.)
function deckRank(d: Deck): number {
  if (d.isDefault) return 0;
  if (d.curriculumId) return 1;
  return 2;
}
const activeDecks = computed(() =>
  decks.value
    .filter((d) => !d.archivedAt)
    .slice()
    .sort((a, b) => deckRank(a) - deckRank(b) || a.name.localeCompare(b.name))
);

const deletingId = ref<string | null>(null);

async function deleteDeck(deck: Deck) {
  if (deck.isDefault || deck.curriculumId || deletingId.value) return;
  const ok = window.confirm(
    `Delete "${deck.name}"? This removes the deck and its ${deck.cardCount} card${
      deck.cardCount === 1 ? '' : 's'
    }.`
  );
  if (!ok) return;
  deletingId.value = deck.id;
  error.value = '';
  try {
    await trpc.deleteDeck.mutate({ id: deck.id });
    decks.value = decks.value.filter((d) => d.id !== deck.id);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to delete deck';
  } finally {
    deletingId.value = null;
  }
}

async function fetchDecks() {
  if (!user.value) return;
  loading.value = true;
  error.value = '';
  try {
    decks.value = (await trpc.listDecks.query()) as Deck[];
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load decks';
  } finally {
    loading.value = false;
  }
}

async function createDeck() {
  const name = nameInput.value.trim();
  if (!name || creating.value) return;
  creating.value = true;
  error.value = '';
  try {
    const deck = await trpc.createDeck.mutate({ name });
    nameInput.value = '';
    router.push(`/decks/${deck.id}`);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to create deck';
  } finally {
    creating.value = false;
  }
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  } else {
    fetchDecks();
  }
});

watch(user, (newUser) => {
  if (newUser) fetchDecks();
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 overflow-y-auto scrollbar-thin">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div
          v-if="isLoading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="user"
          class="space-y-6"
        >
          <!-- New deck -->
          <div class="bg-card rounded-lg shadow-md p-6 border border-border">
            <h2 class="text-lg font-medium mb-3">
              New deck
            </h2>
            <form
              class="flex gap-2"
              @submit.prevent="createDeck"
            >
              <input
                v-model="nameInput"
                type="text"
                placeholder="e.g., Definitions, Spanish vocab, Chess openings..."
                class="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
              <Button
                type="submit"
                :disabled="!nameInput.trim() || creating"
              >
                Create
              </Button>
            </form>
          </div>

          <div class="flex justify-between items-center">
            <h2 class="text-2xl font-semibold">
              Your Decks
            </h2>
            <Button @click="router.push('/cards/new')">
              <Plus class="size-4" />
              New Card
            </Button>
          </div>

          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-4 rounded-md"
          >
            {{ error }}
          </div>

          <div
            v-if="loading"
            class="text-center text-muted-foreground"
          >
            Loading decks...
          </div>

          <div
            v-else-if="activeDecks.length === 0"
            class="text-center text-muted-foreground py-8"
          >
            No decks yet. Create one above to start adding cards.
          </div>

          <div
            v-else
            class="grid gap-4"
          >
            <div
              v-for="deck in activeDecks"
              :key="deck.id"
              class="bg-card rounded-lg shadow-md p-4 border border-border cursor-pointer hover:border-primary/50 transition-colors"
              @click="router.push(`/decks/${deck.id}`)"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-lg font-medium text-foreground">{{ deck.name }}</span>
                <div class="flex items-center gap-2 shrink-0">
                  <span
                    v-if="deck.isDefault"
                    class="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
                  >
                    Default
                  </span>
                  <span
                    v-else-if="deck.curriculumId"
                    class="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
                  >
                    Curriculum
                  </span>
                  <span
                    v-if="deck.dueCount > 0"
                    class="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground"
                  >
                    {{ deck.dueCount }} due
                  </span>
                  <button
                    v-if="!deck.isDefault && !deck.curriculumId"
                    type="button"
                    :disabled="deletingId === deck.id"
                    class="text-muted-foreground hover:text-destructive p-1 rounded disabled:opacity-50"
                    title="Delete deck"
                    @click.stop="deleteDeck(deck)"
                  >
                    <Trash2 class="size-4" />
                  </button>
                </div>
              </div>
              <p class="text-sm text-muted-foreground mt-1">
                {{ deck.cardCount }} card{{ deck.cardCount === 1 ? '' : 's' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
